import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Dialog } from 'primeng/dialog';
import {
  BehaviorSubject,
  filter,
  first,
  forkJoin,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { Blueprint } from '../../../../../../models/blueprint.model';
import {
  CadFile,
  CadFileProcessingStatus,
  CadFileProcessingUpdate,
  CadFileProcessingUpdateSchema,
} from '../../../../../../models/cad-file.model';
import { CadObject } from '../../../../../../models/cad-object.model';
import { QuoteItemData } from '../../../../../../models/quote-item-data.model';
import { CadFileService } from '../../../../../services/cad-file.service';
import { CadProcessingService } from '../../../../../services/cad-processing.service';
import { RealtimeSocketService } from '../../../../../services/realtime/realtime-socket.service';
import { QuoteItemDataEditorService } from '../quote-item-data-editor.service';
import { QuoteItemLinkFileComponent } from './quote-item-link-file/quote-item-link-file.component';
import { QuoteItemSelectTemplateComponent } from './quote-item-select-template/quote-item-select-template.component';
import { QuoteItemUploadFileComponent } from './quote-item-upload-file/quote-item-upload-file.component';

@Component({
  selector: 'app-quote-item-link-cad',
  imports: [
    Dialog,
    QuoteItemUploadFileComponent,
    QuoteItemSelectTemplateComponent,
    QuoteItemLinkFileComponent,
  ],
  templateUrl: './quote-item-link-cad.component.html',
  styleUrl: './quote-item-link-cad.component.scss',
})
export class QuoteItemLinkCadComponent implements OnInit, OnChanges, OnDestroy {
  @Input() initialLinkItems: QuoteItemData[] = [];
  @Input() mode: 'item' | 'blueprint' | 'bulk' = 'item';
  @Input() allBlueprints: Blueprint[] = [];

  @Output() confirmed = new EventEmitter<QuoteItemData[]>();
  @Output() closed = new EventEmitter<void>();

  private quoteItemDataEditorService = inject(QuoteItemDataEditorService, {
    optional: true,
  });
  private cadFileService = inject(CadFileService);
  private cadProcessing = inject(CadProcessingService);
  private realtimeSocket = inject(RealtimeSocketService);

  // ---------------------------------------------------------------------------
  // Upload-flow state (previously in CadFileService)
  // ---------------------------------------------------------------------------
  private cadObjectsBehaviorSubject = new BehaviorSubject<CadObject[]>([]);
  cadObjects$: Observable<CadObject[]> =
    this.cadObjectsBehaviorSubject.asObservable();

  isPreparingCad = signal(false);
  preparationProgress = signal(0);
  preparationStatus = signal('');
  preparationError = signal<string | null>(null);
  preparingCadFileNames = signal<string[]>([]);
  private processingUpdates = new Map<number, CadFileProcessingUpdate>();
  private processingCadFileIds = new Set<number>();
  private processingCadFileNamesById = new Map<number, string>();

  private readonly uploadEndProgress = 10;
  private readonly extractionProgress = 10;
  private readonly generationProgress = 70;
  private readonly viewerPreparationProgress = 10;

  get latestCadObjects(): CadObject[] {
    return this.cadObjectsBehaviorSubject.value;
  }

  clearState(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.cadObjectsBehaviorSubject.next([]);
    this.isPreparingCad.set(false);
    this.preparationProgress.set(0);
    this.preparationStatus.set('');
    this.preparationError.set(null);
    this.preparingCadFileNames.set([]);
    this.processingUpdates.clear();
    this.processingCadFileIds.clear();
    this.processingCadFileNamesById.clear();
  }

  private loadCadFiles(cadFiles: CadFile[]): void {
    this.cadObjectsBehaviorSubject.next([]);
    const allObjects: CadObject[] = cadFiles.flatMap(
      (f) => f.cad_objects ?? [],
    );
    if (allObjects.length === 0) {
      this.failPreparation('No viewable CAD objects were found.');
      return;
    }
    const totalObjects = this.countCadObjects(allObjects);
    const totalObjectsByFile = new Map(
      cadFiles.map((cadFile) => [
        cadFile.id,
        this.countCadObjects(cadFile.cad_objects ?? []),
      ]),
    );
    const loadedObjectsByFile = new Map<number, number>();
    let loadedObjects = 0;
    this.isPreparingCad.set(true);
    this.preparationProgress.set(
      this.uploadEndProgress +
        this.extractionProgress +
        this.generationProgress,
    );
    this.preparationStatus.set(`Preparing CAD for viewing — 0/${totalObjects}`);
    const onObjectLoaded = (obj: CadObject): void => {
      loadedObjects += 1;
      loadedObjectsByFile.set(
        obj.cad_file_id,
        (loadedObjectsByFile.get(obj.cad_file_id) ?? 0) + 1,
      );
      this.preparationProgress.set(
        this.uploadEndProgress +
          this.extractionProgress +
          this.generationProgress +
          this.viewerPreparationOverallProgress(
            totalObjectsByFile,
            loadedObjectsByFile,
          ),
      );
      this.preparationStatus.set(
        `Preparing CAD for viewing — ${loadedObjects}/${totalObjects}`,
      );
    };
    const subscription = this.cadFileService
      .loadCadObjectGeometry(allObjects, { onObjectLoaded })
      .subscribe({
        next: (loadedCadObjects) => {
          this.cadObjectsBehaviorSubject.next(loadedCadObjects);
          this.currentStep.set(2);
          this.finishPreparation();
        },
        error: () => this.failPreparation('Failed to prepare CAD for viewing.'),
      });
    this.subscriptions.add(subscription);
  }

  private hasReadyObjects(cadFile: CadFile): boolean {
    return (
      (cadFile.cad_objects?.length ?? 0) > 0 &&
      cadFile.cad_objects!.some(
        (obj) => (obj.ply_connected_url || obj.ply_disconnected_url) !== null,
      )
    );
  }

  private hasCadObjects(cadFile: CadFile): boolean {
    return (cadFile.cad_objects?.length ?? 0) > 0;
  }

  private countCadObjects(cadObjects: CadObject[]): number {
    return cadObjects.reduce(
      (count, cadObject) =>
        count + 1 + this.countCadObjects(cadObject.children ?? []),
      0,
    );
  }

  private hasUsableObjectsInFiles(cadFiles: CadFile[]): boolean {
    return cadFiles.some((cadFile) => this.hasCadObjects(cadFile));
  }

  private subscriptions = new Subscription();

  // ---------------------------------------------------------------------------

  dialogVisible = false;
  currentStep = signal(1);
  linkItems = signal<QuoteItemData[]>([]);

  // Bulk-mode state
  uploadedCadFiles = signal<CadFile[]>([]);
  selectedBlueprint = signal<Blueprint | null>(null);

  steps = computed(() => {
    if (this.mode === 'bulk') {
      return [
        { number: 1, label: 'Upload' },
        { number: 2, label: 'Select Template' },
      ];
    }
    if (this.mode === 'blueprint') {
      return [
        { number: 1, label: 'Upload' },
        { number: 2, label: 'Select Template' },
        { number: 3, label: 'Link File' },
      ];
    }
    return [
      { number: 1, label: 'Upload' },
      { number: 2, label: 'Link File' },
    ];
  });

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------
  ngOnInit(): void {
    this.dialogVisible = true;
    this.linkItems.set(this.initialLinkItems.map((item) => ({ ...item })));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialLinkItems']) {
      this.linkItems.set(this.initialLinkItems.map((item) => ({ ...item })));
    }
  }

  // ---------------------------------------------------------------------------
  // Stepper helpers
  // ---------------------------------------------------------------------------
  isStepCompleted(stepNumber: number): boolean {
    return this.currentStep() > stepNumber;
  }

  isStepActive(stepNumber: number): boolean {
    return this.currentStep() === stepNumber;
  }

  // ---------------------------------------------------------------------------
  // Step event handlers
  // ---------------------------------------------------------------------------
  onCadFilesUploadRequested(files: File[]): void {
    this.clearState();
    this.preparingCadFileNames.set(
      this.mode === 'bulk'
        ? files.slice(0, 1).map((file) => file.name)
        : files.map((file) => file.name),
    );
    this.isPreparingCad.set(true);
    this.preparationProgress.set(this.uploadEndProgress / 2);
    this.preparationStatus.set(
      `Uploading CAD file${files.length === 1 ? '' : 's'}`,
    );

    const subscription = this.cadFileService.postCadFiles(files).subscribe({
      next: (cadFiles) => this.onCadFilesUploaded(cadFiles),
      error: (error: unknown) =>
        this.failPreparation(
          error instanceof Error
            ? error.message
            : `Failed to upload CAD file${files.length === 1 ? '' : 's'}.`,
        ),
    });
    this.subscriptions.add(subscription);
  }

  onCadFilesUploaded(cadFiles: CadFile[]): void {
    this.processingCadFileNamesById = new Map(
      cadFiles.map((cadFile) => [cadFile.id, cadFile.filename]),
    );
    this.processingCadFileIds = new Set(cadFiles.map((cadFile) => cadFile.id));
    this.processingUpdates = new Map(
      cadFiles.map((cadFile) => [
        cadFile.id,
        {
          id: cadFile.id,
          processing_status: cadFile.processing_status,
          processing_error: cadFile.processing_error || null,
          total_object_count: cadFile.total_object_count,
          completed_object_count: cadFile.completed_object_count,
          failed_object_count: cadFile.failed_object_count,
        },
      ]),
    );
    this.syncActiveCadFileName([...this.processingUpdates.values()]);
    this.isPreparingCad.set(true);
    this.preparationProgress.set(this.uploadEndProgress);
    this.preparationStatus.set('Queued for processing');
    this.preparationError.set(null);

    const subscription = forkJoin(
      cadFiles.map((cadFile) => this.waitForCadFile(cadFile)),
    ).subscribe({
      next: (readyCadFiles) => {
        if (!this.hasUsableObjectsInFiles(readyCadFiles)) {
          this.failPreparation('No viewable CAD objects were found.');
          return;
        }

        if (this.mode === 'bulk') {
          this.uploadedCadFiles.set(readyCadFiles);
          this.currentStep.set(2);
          this.finishPreparation();
          return;
        }

        this.loadCadFiles(
          readyCadFiles.filter((cadFile) => this.hasCadObjects(cadFile)),
        );
      },
      error: (error: unknown) =>
        this.failPreparation(
          error instanceof Error
            ? error.message
            : 'Failed to process CAD file.',
        ),
    });
    this.subscriptions.add(subscription);
  }

  onBlueprintSelected(blueprint: Blueprint | null): void {
    if (this.mode === 'bulk') {
      this.selectedBlueprint.set(blueprint);
      return;
    }

    if (!this.quoteItemDataEditorService) return;
    if (!blueprint) {
      this.linkItems.set([]);
      return;
    }

    const preparedItems =
      this.quoteItemDataEditorService.buildBlueprintItemsForLinking(
        blueprint,
        this.allBlueprints,
      );
    this.linkItems.set(preparedItems);
  }

  onSelectTemplateNext(): void {
    if (this.mode === 'bulk') {
      this.onBulkSave();
      return;
    }
    this.currentStep.set(3);
  }

  onSkipTemplate(): void {
    if (!this.quoteItemDataEditorService) return;
    const cadRoots = this.latestCadObjects.filter(
      (cadObject) => cadObject.parent_id == null,
    );
    const items =
      this.quoteItemDataEditorService.buildItemsFromCadObjects(cadRoots);
    this.linkItems.set(items);
    this.currentStep.set(3);
  }

  onSelectTemplateBack(): void {
    this.linkItems.set([]);
    this.currentStep.set(1);
  }

  onLinkFileBack(): void {
    this.currentStep.set(this.mode === 'blueprint' ? 2 : 1);
  }

  onLinkConfirmed(items: QuoteItemData[]): void {
    this.confirmed.emit(items);
    this.close();
  }

  onDialogHide(): void {
    this.reset();
    this.closed.emit();
  }

  private waitForCadFile(cadFile: CadFile): Observable<CadFile> {
    const isReady =
      this.mode === 'bulk'
        ? this.hasCadObjects(cadFile)
        : this.hasReadyObjects(cadFile);
    if (isReady) {
      return of(cadFile);
    }

    return this.realtimeSocket
      .listen({
        url: `/ws/cad-file/${cadFile.id}`,
        eventType: 'cad.file_update',
        payloadKey: 'cad_file',
        schema: CadFileProcessingUpdateSchema,
      })
      .pipe(
        tap((update) => this.applyProcessingUpdate(update)),
        filter((update) => this.cadProcessing.isTerminal(update)),
        first(),
        switchMap((update) => {
          if (update.processing_status === CadFileProcessingStatus.FAILED) {
            return throwError(
              () =>
                new Error(
                  update.processing_error || 'CAD file processing failed.',
                ),
            );
          }
          return this.cadFileService.getCadFile(cadFile.id);
        }),
        timeout(300000),
      );
  }

  private applyProcessingUpdate(update: CadFileProcessingUpdate): void {
    this.processingUpdates.set(update.id, update);
    const updates = [...this.processingUpdates.values()];
    this.syncActiveCadFileName(updates);

    const activeUpdate = this.activeProcessingUpdate(updates) ?? updates[0];
    if (activeUpdate) {
      this.preparationStatus.set(this.cadProcessing.statusText(activeUpdate));
      this.preparationProgress.set(this.processingOverallProgress(updates));
    }
  }

  private processingOverallProgress(
    updates: CadFileProcessingUpdate[],
  ): number {
    const fileCount = this.processingCadFileIds.size;
    if (fileCount === 0) {
      return this.uploadEndProgress;
    }

    const progressByFile = new Map(
      updates.map((update) => [update.id, update]),
    );
    const phaseProgress = [...this.processingCadFileIds].reduce(
      (total, cadFileId) =>
        total + this.processingPhaseProgress(progressByFile.get(cadFileId)),
      0,
    );
    return Math.round(this.uploadEndProgress + phaseProgress / fileCount);
  }

  private syncActiveCadFileName(updates: CadFileProcessingUpdate[]): void {
    if (this.mode !== 'bulk') {
      return;
    }

    const activeUpdate = this.activeProcessingUpdate(updates);
    const cadFileName = activeUpdate
      ? this.processingCadFileNamesById.get(activeUpdate.id)
      : undefined;
    this.preparingCadFileNames.set(cadFileName ? [cadFileName] : []);
  }

  private activeProcessingUpdate(
    updates: CadFileProcessingUpdate[],
  ): CadFileProcessingUpdate | undefined {
    return (
      updates.find(
        (item) => item.processing_status === CadFileProcessingStatus.FAILED,
      ) ??
      updates.find(
        (item) => item.processing_status === CadFileProcessingStatus.EXTRACTING,
      ) ??
      updates.find(
        (item) => item.processing_status === CadFileProcessingStatus.PROCESSING,
      ) ??
      updates.find(
        (item) => item.processing_status === CadFileProcessingStatus.QUEUED,
      )
    );
  }

  private processingPhaseProgress(
    update: CadFileProcessingUpdate | undefined,
  ): number {
    if (!update) {
      return 0;
    }

    switch (update.processing_status) {
      case CadFileProcessingStatus.EXTRACTING:
        return this.extractionProgress;
      case CadFileProcessingStatus.PROCESSING:
        return (
          this.extractionProgress +
          (update.total_object_count === 0
            ? 0
            : ((update.completed_object_count + update.failed_object_count) /
                update.total_object_count) *
              this.generationProgress)
        );
      case CadFileProcessingStatus.COMPLETED:
      case CadFileProcessingStatus.PARTIAL_FAILURE:
        return this.extractionProgress + this.generationProgress;
      default:
        return 0;
    }
  }

  private viewerPreparationOverallProgress(
    totalObjectsByFile: Map<number, number>,
    loadedObjectsByFile: Map<number, number>,
  ): number {
    const fileCount = totalObjectsByFile.size;
    if (fileCount === 0) {
      return 0;
    }

    const completedPortions = [...totalObjectsByFile].reduce(
      (total, [cadFileId, totalObjects]) =>
        total +
        Math.min(1, (loadedObjectsByFile.get(cadFileId) ?? 0) / totalObjects),
      0,
    );
    return Math.round(
      (completedPortions / fileCount) * this.viewerPreparationProgress,
    );
  }

  private finishPreparation(): void {
    this.isPreparingCad.set(false);
    this.preparationProgress.set(0);
    this.preparationStatus.set('');
    this.preparingCadFileNames.set([]);
    this.processingUpdates.clear();
    this.processingCadFileIds.clear();
    this.processingCadFileNamesById.clear();
  }

  private failPreparation(message: string): void {
    this.isPreparingCad.set(false);
    this.preparationProgress.set(0);
    this.preparationStatus.set('');
    this.preparingCadFileNames.set([]);
    this.preparationError.set(message);
    this.processingUpdates.clear();
    this.processingCadFileIds.clear();
    this.processingCadFileNamesById.clear();
  }

  private onBulkSave(): void {
    if (!this.quoteItemDataEditorService) return;
    const blueprint = this.selectedBlueprint();
    if (!blueprint) return;

    const allItems =
      this.quoteItemDataEditorService.buildBulkItemsFromBlueprint(
        blueprint,
        this.allBlueprints,
        this.uploadedCadFiles(),
      );

    this.confirmed.emit(allItems);
    this.close();
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------
  private close(): void {
    this.dialogVisible = false;
  }

  private reset(): void {
    this.currentStep.set(1);
    this.linkItems.set([]);
    this.uploadedCadFiles.set([]);
    this.selectedBlueprint.set(null);
    this.clearState();
  }
}
